import type { PersistentAgentStatus } from '@cortex/types';

export type ThreadLifecycleAction = 'pause' | 'resume' | 'stop';

/**
 * Confirm copy for a thread reset — shared by every surface that offers it (the
 * thread detail page and the orchestration graph's chat panel), so the two can
 * never drift into describing the same irreversible action differently.
 *
 * Deliberately says what is DESTROYED and what SURVIVES. The survivor clause is
 * the load-bearing half: saved memories live on the agent (`AgentMemory`, no FK
 * to the thread), so a reset never touches them — and an operator who assumes
 * otherwise will avoid resetting a thread they should reset. Note this is about
 * saved memories, not the thread's Mastra working/observational memory, which
 * IS wiped along with the conversation.
 */
export const threadResetConfirmTitle = 'Reset this thread?';
export const threadResetConfirmMessage =
  'This permanently deletes everything in this thread — messages, facts, events, tool calls, ' +
  'notes, tasks and history — then rebuilds it from scratch against the agent’s current system ' +
  'prompt, MCP connections and permissions. Only the thread’s id and name are kept. ' +
  'Memories this thread saved are NOT deleted: they belong to the agent, not the thread, and stay ' +
  'available to it after the reset. This cannot be undone.';
export const threadResetConfirmLabel = 'Wipe and rebuild';

export function threadLifecyclePath(
  agentId: string,
  threadId: string,
  action: ThreadLifecycleAction,
): string {
  return `v1/persistent-agent-groups/${agentId}/agents/${threadId}/${action}`;
}

/** Terminal DB state is monotonic and must beat a stale live-stream frame. */
export function resolvedThreadStatus(
  streamedStatus: PersistentAgentStatus | null,
  persistedStatus: PersistentAgentStatus | null,
): PersistentAgentStatus | null {
  if (persistedStatus === 'STOPPED' || persistedStatus === 'FAILED') return persistedStatus;
  return streamedStatus ?? persistedStatus;
}

/** REST mutation status wins while the host's SSE frame catches up. */
export function displayedThreadStatus(
  streamedStatus: PersistentAgentStatus | null,
  mutationStatus: PersistentAgentStatus | null,
): PersistentAgentStatus | null {
  return mutationStatus ?? streamedStatus;
}
